/**
 * EVERYTHING WRONG WITH AN EXPERIENCE, in one list, from outside every block.
 *
 * Three sources, and keeping them three is deliberate: they are found in different places and the
 * panel groups them differently, but they share one shape so the gate can count them without
 * caring which is which.
 *
 *  - **Block issues**, via `issuesForBlock` — the block's own checks, unchanged. Not recomputed
 *    here and never second-guessed.
 *  - **Flow card problems**, via `getCardProblem` — a routing rule or a Change that cannot run.
 *    These are reported TODAY and shown only inside the task's own flow panel, which is why a
 *    deleted goto target is a silent failure from the board: the pause exists and nobody sees it.
 *  - **Unreachable tasks** — nothing routes to them. The board already draws "No entrance" on the
 *    card; this makes it a problem the gate can read.
 *
 * ## Settled, always
 *
 * `applyIssuePolicy(…, 'settled')` rather than `'editing'`: the board is settled by definition,
 * since nobody is mid-edit in a task they are not looking at. Supersession still applies — a
 * catch-all standing aside for a specific check removes a duplicate row here exactly as it does in
 * an editor. `shownInPlace` is deliberately ignored: it is a property of the collapsed card's
 * one-line status bar, read in exactly one place, and the board has no such surface.
 */
import { deriveGraph, type AnswerRefAlias, type GraphTask } from '@agavi/blocks';

import type { Block, Task, TaskVariable } from '@/types/experience';

import {
  applyIssuePolicy, memoizedByKey, type BlockIssue, type IssueCache, type IssueContext,
} from '@/components/task/block-issues';
import { BLOCK_META } from '@/components/task/block-library';
import { issuesForBlock } from '@/lib/block-issue-dispatch';
import { flowCardKey, getCardProblem, type CardProblemKind } from '@/lib/flow-utils';
import { recallIssuesFor } from '@/lib/recall-issues';


/**
 * `summary` is the task's SUMMARY FEEDBACK — the blocks a teacher writes on a flow exit.
 *
 * Its own kind rather than `block`, and the owner's words for why: "block is misleading ...
 * summary feedback isn't a block". They are authored in blocks and stored on the flow, they
 * are not in `task.blocks`, and nothing that resolves a block id will find them.
 */
export type ProblemSource = 'block' | 'flow' | 'task' | 'summary';

export interface ExperienceProblem {
  /** Stable across recomputes, so a panel selection survives a re-render. */
  id: string;
  taskId: string;
  /** Absent for a flow or task-level problem, which belong to no single block. */
  blockId?: string;
  /**
   * Which exit this came from, on a `summary` row.
   *
   * Carried rather than parsed back out of `id`, which is the habit this file already keeps
   * for everything else: an id is for identity, and a second reader of it is a second place to
   * get the format wrong.
   */
  exitSlot?: string;
  /** The block INSIDE that exit's content, so the dialog can scroll to it and flash it. Not
   *  `blockId`, which every other reader takes to mean a block on the task's canvas. */
  exitBlockId?: string;
  /**
   * WHERE, when that is somewhere other than the task's canvas — "Summary feedback".
   *
   * The owner's order: *"summary feedback · sound · transcript"*, place before block before
   * field, where every other row is block before field. It used to overwrite the issue's own
   * category with "Summary feedback", which put the place where the field belongs and lost the
   * field altogether: a row read "Sound · Summary feedback" and never said transcript.
   */
  scope?: string;
  /** The flow card this came from (`flowCardKey`), so a click can open the panel on it. */
  flowCardId?: string;
  /**
   * WHICH BLOCK, in the teacher's own words — "MCQ 1", not "Question".
   *
   * The owner's: *"I see 'question text is blank' from 'question'. That's not helpful."* And it
   * is not: `category` says which AREA of a block a fault sits in, which is exactly the right
   * second line inside an editor where the block is the thing you are looking at, and says almost
   * nothing on a board listing every task at once. The name is what distinguishes one of three
   * MCQs from the other two.
   *
   * `block.name` is the teacher-facing identifier, auto-assigned at creation ("Dropdown 2") or
   * renamed; absent only on a block old enough to predate it, where the type label stands in.
   */
  blockName?: string;
  /**
   * The block's TYPE, in the reader's language — "GPS", not "gps", and never shown.
   *
   * It is here for the filter alone. `blockName` falls back to the type only when a block has no
   * name of its own, so a teacher who renamed "GPS 1" to "Location" could no longer find their GPS
   * problems by typing "gps": the word had been replaced by the rename. Carried beside the name
   * rather than derived in the filter, because the filter is handed rows and never sees a block.
   */
  blockType?: string;
  source: ProblemSource;
  issue: BlockIssue;
}

export interface ExperienceProblemSet {
  rows: ExperienceProblem[];
  errors: number;
  warnings: number;
  /** Errors gate publication. One anywhere is enough — an error is something guaranteed to fail
   *  for the student, so there is no such thing as an experience that is publishable except for
   *  one task. */
  blocksPublication: boolean;
}

/**
 * HOW LOUDLY EACH FLOW FAULT SPEAKS — one table, because the owner has to be able to change it.
 *
 * `project-flow-publication-gating-plan` records the decision to gate on flow problems and
 * explicitly leaves the TIERS unsettled, with a note to raise them rather than assume. These are a
 * defensible default and nothing more: the three errors are faults where the rule can never run,
 * and the two warnings are faults where the flow still works and the teacher may already know.
 */
export const FLOW_PROBLEM_SEVERITY: Record<CardProblemKind, BlockIssue['severity']> = {
  /** The task this routes to was deleted: the route goes nowhere. */
  targetDeleted: 'error',
  /** The conditions contradict, or name something gone: it can never hold. */
  broken: 'error',
  /** No conditions set, so the event can never fire — an unfinished card, left behind. */
  incomplete: 'error',
  /** A Change with nothing to change. */
  noEffects: 'error',
  /** A condition changed meaning. Acknowledgeable, and may still be what the teacher wants. */
  drifted: 'warning',
  /** Preempted by an earlier rule. Dead, but the flow around it still works. */
  unreachable: 'warning',
};

const FLOW_PROBLEM_KIND: Record<CardProblemKind, BlockIssue['kind']> = {
  targetDeleted: 'defect', broken: 'defect', drifted: 'defect', unreachable: 'defect',
  incomplete: 'incompletion', noEffects: 'incompletion',
};

/** The message key each kind already has, so the panel says what the flow panel says. */
const FLOW_PROBLEM_KEY: Record<CardProblemKind, string> = {
  targetDeleted: 'task.flow.rule.pausedTargetDeleted',
  broken: 'task.flow.rule.pausedBroken',
  drifted: 'task.flow.rule.pausedDrifted',
  unreachable: 'task.flow.rule.paused',
  incomplete: 'task.flow.rule.pausedIncomplete',
  noEffects: 'task.flow.rule.pausedNoEffects',
};

export function experienceProblems(args: {
  tasks: Task[];
  /** Experience-level variables, in force for every task on top of the task's own. */
  variables?: TaskVariable[];
  t: IssueContext['t'];
  locale: string;
  /**
   * The Variables rung, passed through to every block's context so the board can see a variable
   * sitting in a block whose rung does not support one.
   *
   * The BOARD is where this matters most: the hole it closes is content that arrived without an
   * authoring action — pasted, duplicated, pulled from another task, or left behind when the rung
   * was lowered — so the task holding it is very often not the one being looked at.
   */
  variableRung?: number;
  /**
   * The experience's aliases, for the recall checks this board had none of.
   *
   * Absent means the caller cannot supply them and the recall rows are simply not produced —
   * the state this board was permanently in before `aliases` existed as an argument.
   */
  aliases?: readonly AnswerRefAlias[];
  /**
   * The Answer Recall rung, for the check that asks whether a recall may be in a block type at
   * all. Same contract as `variableRung`: absent means the caller does not know, and that one
   * check stays silent rather than guessing in either direction.
   */
  recallRung?: number;
}): ExperienceProblemSet {
  const {
    tasks, variables: experienceVariables = [], t, locale, variableRung, aliases, recallRung,
  } = args;
  const rows: ExperienceProblem[] = [];

  /* LOCAL, not transitive, matching the board's own rule: flag the task that is actually orphaned
     and not everything downstream of it. A task IS entered if a predecessor flows into it, even
     where that predecessor is itself unreachable — the predecessor gets its own row. */
  const graph = deriveGraph(tasks as unknown as GraphTask[]);
  const entered = new Set<string>();
  for (const e of graph.edges) if (e.toId) entered.add(e.toId);

  for (const task of tasks) {
    const blocks = task.blocks ?? [];
    const variables = [...(task.variables ?? []), ...experienceVariables];

    for (const block of blocks) {
      const ctx: IssueContext = { t, locale, variables, blockId: block.id, blocks, variableRung };
      /*
       * RECALL IS CHECKED HERE TOO, and was not. Its four checks were React hooks living in
       * `IssuesPanel`, so they ran in a block editor and nowhere else: a dead reference was
       * drawn on the card and never reached this list, which is the one the publication gate
       * counts. Both of these are errors, so both were errors that did not hold publication.
       */
      const recall = aliases
        ? recallIssuesFor(block, {
          tasks: tasks as never, aliases, currentTaskId: task.id, t, rung: recallRung,
        })
        : [];
      for (const issue of applyIssuePolicy([...cachedIssues(block, ctx), ...recall], 'settled')) {
        rows.push({
          id: `${task.id}:${block.id}:${issue.key}`,
          taskId: task.id, blockId: block.id, source: 'block', issue,
          blockName: blockLabel(block, t), blockType: blockTypeLabel(block, t),
        });
      }
    }

    /*
     * SUMMARY FEEDBACK IS CHECKED TOO, and was not.
     *
     * Its blocks run the same checks as any other — an image with no alt text raises the same
     * `alt-text-missing` error, and the field goes red in the authoring modal — but this census
     * walked `task.blocks` and nothing else, so nothing ever collected them. The error showed
     * while you were typing and then vanished: absent from this panel, and absent from the gate,
     * so an experience shipped with an undescribed image on a summary screen.
     *
     * No `blockId`: it would send "Go to" looking for a block that is in no task. The slot
     * travels instead, which is what a reader needs to find it.
     *
     * `blocks` is the exit's OWN list, not the task's. These are each other's siblings; the
     * canvas blocks are not, and a check that compares against them would be answering a
     * question about the wrong surface.
     */
    for (const [slot, content] of Object.entries(task.flow?.exits ?? {})) {
      const exitBlocks = content?.blocks ?? [];
      for (const block of exitBlocks) {
        const ctx: IssueContext = {
          t, locale, variables, blockId: block.id, blocks: exitBlocks as typeof blocks, variableRung,
        };
        for (const issue of applyIssuePolicy(cachedIssues(block as typeof blocks[number], ctx), 'settled')) {
          rows.push({
            id: `${task.id}:exit:${slot}:${block.id}:${issue.key}`,
            taskId: task.id, exitSlot: slot, exitBlockId: block.id,
            blockName: blockLabel(block as typeof blocks[number], t),
            blockType: blockTypeLabel(block as typeof blocks[number], t),
            scope: t('experience.problems.summaryFeedback'),
            source: 'summary',
            issue,
          });
        }
      }
    }

    /* A PATHWAY TASK NOTHING ROUTES TO. The main spine is entered by being next in sequence, so
       only a detour can be orphaned — the same test the card's own "No entrance" makes. The owner's
       call: fatal, because a dangling task is a mess anyone else opening the experience has to
       interpret. */
    if (task.pathwayOf && !entered.has(task.id)) {
      rows.push({
        id: `${task.id}:no-entrance`, taskId: task.id, source: 'task',
        issue: {
          key: 'task-no-entrance', severity: 'error', kind: 'defect',
          /* The board's own words for the same fault, so the panel and the card agree. */
          category: t('task.bottomBar.flow'), detail: t('experience.builder.noEntrance'),
        },
      });
    }

    /* Same test as the changes below: a rule with no id is not a card the panel can show. */
    const rules = (task.flow?.eventRules ?? [])
      .filter((r) => !!r && typeof r === 'object' && !Array.isArray(r) && typeof r.id === 'string' && !!r.id);
    for (const rule of rules) {
      const problem = getCardProblem(rule, blocks, true, { action: rule.action }, tasks);
      if (problem) rows.push(flowRow(task.id, flowCardKey('rule', rule.id), problem.kind, t));
    }
    /*
     * ONLY A CARD THAT EXISTS, which means one with an id of its own.
     *
     * A real task was found holding `changes: [[]]` — junk from an old copy. `normalizeFlowConfig`
     * drops it (it is not an object), so the flow panel never draws a card for it; read raw, as
     * this does, it looked like a Change with no conditions, and the board reported a paused card
     * that held publication and that clicking landed on a panel with nothing in it. The board's
     * copy of it has even been seen as `{}` rather than `[]`, so the test is not the shape but
     * whether the thing can be addressed at all: no id, no card, nothing a teacher could fix.
     */
    const changes = (task.flow?.changes ?? [])
      .filter((c) => !!c && typeof c === 'object' && !Array.isArray(c) && typeof c.id === 'string' && !!c.id);
    for (const change of changes) {
      const problem = getCardProblem(change, blocks, true, { effects: change.effects });
      if (problem) rows.push(flowRow(task.id, flowCardKey('change', change.id), problem.kind, t));
    }
  }

  const errors = rows.filter(r => r.issue.severity === 'error').length;
  return { rows, errors, warnings: rows.length - errors, blocksPublication: errors > 0 };
}

/**
 * ONE RECOMPUTE PER BLOCK PER CHANGE, because the honest answer is also the expensive one.
 *
 * Issues are RECALCULATED and never read back from storage — an issue is not a property of its
 * block (it depends on experience variables, flow targets and recalls into other tasks), a release
 * that changes a check would invalidate every stored set, and a stored copy is a second source of
 * truth kept in step by hand. But twenty tasks of eight blocks is 160 computations, some of them
 * variable sweeps, and the hitch would land the moment the aspect opens.
 *
 * `block.data` is a sound cache key: every write replaces it wholesale with a fresh object, so
 * identity changing IS the block changing. The deps cover what the computation reads besides —
 * `memoizedByKey` compares them by reference, as every dependency array here does.
 *
 * A WeakMap, so a deleted block's entry goes with it.
 */
const issueCache: IssueCache<BlockIssue[]> = new WeakMap();

function cachedIssues(block: Block, ctx: IssueContext): BlockIssue[] {
  const data = block.data as object | undefined;
  if (!data) return issuesForBlock(block, ctx);
  return memoizedByKey(issueCache, data, [ctx.t, ctx.locale, ctx.variables, ctx.blocks], () =>
    issuesForBlock(block, ctx));
}

/**
 * THE SAME ROWS, GROUPED THE WAY THE BOARD READS.
 *
 * By task, in BOARD order, with a pathway's tasks immediately after the task they branch from —
 * so the panel tracks the board rather than inventing an order of its own. A collapsed pathway is
 * one row carrying its members' counts, because its cards are genuinely hidden (the layout drops
 * them) and there is nothing to mark until it opens.
 */
export interface ProblemGroup {
  taskId: string;
  title: string;
  /** This task sits inside a pathway that is currently collapsed. */
  collapsedInto?: string;
  rows: ExperienceProblem[];
  errors: number;
  warnings: number;
}

export function groupProblems(
  set: ExperienceProblemSet,
  tasks: { id: string; title?: string; pathwayId?: string | null; pathwayCollapsed?: boolean }[],
): ProblemGroup[] {
  const byTask = new Map<string, ExperienceProblem[]>();
  for (const r of set.rows) {
    const list = byTask.get(r.taskId);
    if (list) list.push(r); else byTask.set(r.taskId, [r]);
  }
  const groups: ProblemGroup[] = [];
  for (const task of tasks) {
    const rows = byTask.get(task.id);
    if (!rows?.length) continue;
    const errors = rows.filter(r => r.issue.severity === 'error').length;
    groups.push({
      taskId: task.id,
      title: task.title ?? '',
      collapsedInto: task.pathwayCollapsed ? (task.pathwayId ?? undefined) : undefined,
      rows, errors, warnings: rows.length - errors,
    });
  }
  return groups;
}

/**
 * THE PANEL'S FILTER, over the groups it already has.
 *
 * A board of sixty problems is read one KIND at a time — every alt text, every flow fault — and
 * the owner asked for exactly that: *"it might be good to be able to filter, say, for flow problems
 * or alt-text problems"*. One text box rather than a taxonomy of checkboxes, because the kinds are
 * not a fixed list: the thing a teacher types ("alt", "flow", "transcript", "MCQ 1", a task title)
 * is already written somewhere in the row, so matching the row's own words covers every kind there
 * is and every kind added later.
 *
 * Terms are ANDed, so "alt soil" is every alt text problem in the soil tasks.
 *
 * The BOARD is left alone. Filtering the cards' marks too would make the aspect disagree with
 * itself the moment a filter is on — a card drawn clean while the panel knows better — and the
 * marks are what makes the board readable in the first place.
 */
export type ProblemSeverityFilter = 'all' | 'error' | 'warning';

/* Fold every run of punctuation into a space, both sides, so "alt-text" finds "Alt Text" and
   "alt text" finds "alt-text". The teacher is typing a fault, not a key. */
const foldForSearch = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/**
 * The block a row is about, as a reader needs it: the teacher's name, and the TYPE in brackets
 * when the name no longer says it.
 *
 * "GPS 1" says both already, so "GPS 1 (GPS)" would be noise; "Thingy" says neither, and a panel
 * that only said "Thingy" left a reader to open the task to find out what they were looking at.
 * The test is whether the name already contains the type, not whether they are equal.
 */
export function blockLabelWithType(r: Pick<ExperienceProblem, 'blockName' | 'blockType'>): string | undefined {
  if (!r.blockName || !r.blockType) return r.blockName;
  const name = foldForSearch(r.blockName);
  const type = foldForSearch(r.blockType);
  return !type || name.includes(type) ? r.blockName : `${r.blockName} (${r.blockType})`;
}

export function filterGroups(
  groups: ProblemGroup[],
  { query, severity }: { query: string; severity: ProblemSeverityFilter },
): ProblemGroup[] {
  const terms = foldForSearch(query).split(' ').filter(Boolean);
  if (!terms.length && severity === 'all') return groups;
  const out: ProblemGroup[] = [];
  for (const g of groups) {
    const rows = g.rows.filter((r) => {
      if (severity !== 'all' && r.issue.severity !== severity) return false;
      if (!terms.length) return true;
      /* The task's title is part of the row's text, not a separate axis: "soil" is as natural a
         way to narrow this list as "alt text" is, and a reader typing it means the same thing. */
      const hay = ` ${foldForSearch([r.issue.detail, r.issue.category, r.blockName, r.blockType, r.scope, g.title].filter(Boolean).join(' '))}`;
      /* A term matches the START of a word, not any run of letters inside one: "alt" means alt
         text, and a plain substring match had it finding every "Default" on the board. Still a
         prefix, so "transc" reaches "transcript" before the word is typed out. */
      return terms.every((term) => hay.includes(` ${term}`));
    });
    if (!rows.length) continue;
    const errors = rows.filter((r) => r.issue.severity === 'error').length;
    out.push({ ...g, rows, errors, warnings: rows.length - errors });
  }
  return out;
}

/** What each task's cards need to draw, derived from the same rows the panel lists. */
export function summariseByTask(set: ExperienceProblemSet): Map<string, {
  blockErrors: Set<string>; blockWarnings: Set<string>; errors: number; warnings: number;
}> {
  const out = new Map<string, { blockErrors: Set<string>; blockWarnings: Set<string>; errors: number; warnings: number }>();
  for (const r of set.rows) {
    let entry = out.get(r.taskId);
    if (!entry) { entry = { blockErrors: new Set(), blockWarnings: new Set(), errors: 0, warnings: 0 }; out.set(r.taskId, entry); }
    if (r.issue.severity === 'error') entry.errors++; else entry.warnings++;
    if (r.blockId) {
      /* An ERROR wins: a block with both is drawn as the louder one, and the warning set never
         holds a block the error set already has, so the card never has to decide. */
      if (r.issue.severity === 'error') { entry.blockErrors.add(r.blockId); entry.blockWarnings.delete(r.blockId); }
      else if (!entry.blockErrors.has(r.blockId)) entry.blockWarnings.add(r.blockId);
    }
  }
  return out;
}

/** What KIND of block this is, in the reader's language. `blockLabel` falls back to this when a
 *  block has no name; a row carries it either way, so a rename cannot hide a type from the filter. */
function blockTypeLabel(block: Block, t: IssueContext['t']): string {
  const meta = BLOCK_META[block.type];
  return meta ? t(meta.labelKey) : block.type;
}

/** A block as a teacher names it. The auto-assigned name where there is one, and the type's own
 *  label where there is not — a block old enough to predate `name`. */
function blockLabel(block: Block, t: IssueContext['t']): string {
  return block.name?.trim() || blockTypeLabel(block, t);
}

function flowRow(taskId: string, cardId: string, kind: CardProblemKind, t: IssueContext['t']): ExperienceProblem {
  return {
    id: `${taskId}:${cardId}:${kind}`,
    taskId, source: 'flow', flowCardId: cardId,
    issue: {
      key: `flow-${kind}`,
      severity: FLOW_PROBLEM_SEVERITY[kind],
      kind: FLOW_PROBLEM_KIND[kind],
      category: t('task.bottomBar.flow'),
      detail: t(FLOW_PROBLEM_KEY[kind]),
    },
  };
}
